import { Mail, Plus, X } from "lucide-react";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { normalizeRecipient, validateRecipient } from "@/lib/recipients";

// The recipient list for a usage report: chips + an entry box, with the address
// rule enforced where the address is TYPED.
//
// Validating only the array on save is not enough — by then a blank or
// malformed entry is already in it. Every way in goes through addRecipient():
// the Add button, Enter, and the parent's save path (which commits a
// half-typed draft through commitPending() in lib/recipients). So the list this
// component hands back cannot contain "", "   ", or an address the API would
// reject.
//
// The parent owns both the list and the draft, because the two pages save
// differently: the company report persists as you edit, a platform schedule has
// an explicit Save that must fold the draft in first.
export default function RecipientsField({
  recipients,
  onChange,
  pending,
  onPendingChange,
  error,
  onError,
  placeholder = "name@company.com",
  emptyHint = "No recipients yet.",
  disabled = false,
}) {
  function addRecipient() {
    const message = validateRecipient(pending, recipients);
    if (message) {
      onError(message);
      return;
    }
    onChange([...recipients, normalizeRecipient(pending)]);
    onPendingChange("");
    onError("");
  }

  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Recipients
        {/* ALWAYS shown. The marker used to appear only while the automatic
            report was on, so the same field looked optional in one state and
            required in another — for a rule that does not vary. aria-hidden
            because `required` on the input is what a screen reader announces. */}
        <span className="text-rose-500" aria-hidden="true"> *</span>
      </p>
      <div className="flex items-start gap-2">
        {/* Input renders a display:block <label>, so it has to be given flex-1
            from the outside or it sizes to its content. */}
        <div className="min-w-0 flex-1">
          <Input
            type="email"
            aria-label="Recipient email address"
            icon={Mail}
            placeholder={placeholder}
            value={pending}
            error={error}
            required
            disabled={disabled}
            onChange={(e) => { onPendingChange(e.target.value); onError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
          />
        </div>
        <Button variant="outline" onClick={addRecipient} aria-label="Add recipient"
          disabled={disabled} className="h-11 shrink-0 px-4">
          <Plus size={16} />
        </Button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {recipients.map((address) => (
          <span key={address}
            className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-white/10 dark:text-slate-200">
            {address}
            <button type="button" aria-label={`Remove ${address}`} disabled={disabled}
              onClick={() => onChange(recipients.filter((r) => r !== address))}
              className="opacity-50 hover:opacity-100">
              <X size={12} />
            </button>
          </span>
        ))}
        {recipients.length === 0 && <span className="text-xs text-slate-400">{emptyHint}</span>}
      </div>
    </div>
  );
}
